'use client'

import Link from 'next/link'
import { useLayoutEffect, useRef, useState } from 'react'
import { InfoIcon, Loader2Icon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { BillMarquee } from '@/components/legislative-tracker/BillMarquee'
import { Media } from '@/components/Media'
import { RevealFade, RevealP, RevealSection } from '@/components/RevealSection'
import { SectionHero } from '@/components/SectionHero'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBills } from '@/hooks/useBills'
import { cn } from '@/utilities/ui'

import TopicContent from './components/TopicContent'

import type { Bill, FeaturedBills as FeaturedBillsBlockType, Topic } from '@/payload-types'

export const FeaturedBillsClient = ({
  hero,
  featuredBills,
  topics,
}: {
  hero: FeaturedBillsBlockType['hero']
  featuredBills: Bill[]
  topics: Topic[]
}) => {
  const [isTopicsOpen, setIsTopicsOpen] = useState(false)
  const topicsPanelRef = useRef<HTMLDivElement>(null)
  const { media } = hero || {}

  useLayoutEffect(() => {
    if (!isTopicsOpen) return
    const id = requestAnimationFrame(() => {
      topicsPanelRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [isTopicsOpen])
  const hasGlobalBills = featuredBills?.length > 0

  // Fallback: fetch featured bills from front API when global field is not set
  const { bills: fallbackBills, loading: fallbackLoading } = useBills({
    limit: 12,
    featuredOnly: true,
  })

  const bills = hasGlobalBills ? featuredBills : fallbackBills
  const showLoading = !hasGlobalBills && fallbackLoading
  const hasBills = bills.length > 0

  return (
    <div className="relative border-y border-input bg-input bg-[url('/media/grid-texture.png')] bg-cover bg-repeat py-14">
      <SectionHero {...hero} />
      <RevealSection
        delay={0}
        stagger
        className="relative z-20 wrapper mb-16 flex flex-wrap justify-between gap-3"
      >
        <h2 className="font-family-body text-2xl font-semibold text-foreground sm:text-3xl">
          Featured Bills
        </h2>
        <div className="flex items-center gap-2">
          <DropdownMenu open={isTopicsOpen} onOpenChange={setIsTopicsOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="iconOutline" icon={false}>
                <span className="sr-only">Topics</span>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={isTopicsOpen ? 'close' : 'open'}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.1, ease: 'easeOut' }}
                  >
                    {isTopicsOpen ? <XIcon className="size-5" /> : <InfoIcon className="size-5" />}
                  </motion.span>
                </AnimatePresence>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-75" align="end">
              <div
                ref={topicsPanelRef}
                tabIndex={-1}
                role="region"
                aria-label="Topics"
                className="outline-none focus-visible:outline-none"
              >
                <TopicContent topics={topics} />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild>
            <Link href="/legislative-tracker/bills/all">View all bills</Link>
          </Button>
        </div>
      </RevealSection>
      <RevealFade
        delay={0.1}
        className={cn(
          'pointer-events-none absolute -top-50 left-1/2 z-0 mx-auto block size-full max-w-[1129px] -translate-x-1/2',
        )}
      >
        {media && typeof media === 'object' && (
          <Media
            loading="eager"
            resource={media}
            context={'hero'}
            htmlelement={null}
            pictureClassName="visible absolute inset-0 block size-full overflow-hidden object-contain opacity-40 transition-all duration-500 ease-in"
            imgClassName="size-full object-contain object-center"
          />
        )}
      </RevealFade>
      {showLoading ? (
        <div className="flex h-full min-h-160 items-start justify-center gap-2">
          <RevealP delay={0.2} className="flex items-center gap-2 text-sm text-primary">
            Loading featured bills...
            <Loader2Icon className="size-4 animate-spin text-primary" />
          </RevealP>
        </div>
      ) : hasBills ? (
        <RevealSection delay={0.2}>
          <BillMarquee bills={bills} topics={topics} />
        </RevealSection>
      ) : (
        <div className="flex h-full min-h-160 items-start justify-center gap-2">
          <RevealP delay={0.2} className="font-body text-sm text-primary">
            No featured bills found
          </RevealP>
        </div>
      )}
    </div>
  )
}
